// ============================================================
// JavaScriptAssignment.jsx
// ============================================================
// Junior-dev Notes:
// - Logic: Focuses on ES6+, Closures, Promises, and DOM.
// - UI: Goldenrod accents (#E5C95F) on Neural Purple (#100720).
// - Strategy: 30 Master challenges to build "Interview-Ready" logic.
// - Branding: prodevconsult.com | Finnish Engineering Standards
// ============================================================

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    Chip,
} from "@mui/material";

/* Icons */
import { AiFillBulb } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import JavascriptIcon from "@mui/icons-material/Javascript";
import TerminalIcon from "@mui/icons-material/Terminal";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1579403104002-48744a5dd3ad";

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.92), rgba(16,7,32,0.96)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    fontFamily: "'Inter', sans-serif",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(255,255,255,0.03)",
    borderRadius: "16px",
    padding: theme.spacing(4),
    marginBottom: "25px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
    border: "1px solid rgba(229, 201, 95, 0.15)",
    color: "white",
    transition: "0.3s ease",
    "&:hover": {
        border: "1px solid rgba(229, 201, 95, 0.4)",
        backgroundColor: "rgba(255,255,255,0.05)",
    }
}));

const QuestionItem = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    "&:last-child": { borderBottom: "none" },
    "&:hover": { backgroundColor: "rgba(255,255,255,0.02)" },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#E5C95F",
    color: "#100720",
    fontWeight: 800,
    padding: "14px 45px",
    borderRadius: "4px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "#FFF0A5",
        transform: "translateY(-3px)",
        boxShadow: "0 10px 20px rgba(229, 201, 95, 0.2)",
    },
}));

/* ── Main Component ──────────────────────────────────────── */

export default function JavaScriptAssignment() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleCopy = (questions, index) => {
        const text = questions.join("\n");
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const assignmentData = useMemo(() => [
        {
            title: "ES6+ Fundamentals & Scoping",
            questions: [
                "Explain the difference between var, let, and const with code examples.",
                "Implement a function that uses Rest and Spread operators to merge user objects.",
                "Destructure a nested JSON response from a dummy API.",
                "Create a Template Literal function that renders an HTML list from an array.",
                "Demonstrate the 'Temporal Dead Zone' in JavaScript scoping."
            ]
        },
        {
            title: "Functional Programming & Arrays",
            questions: [
                "Use .map() and .filter() to extract active users over age 18 from a list.",
                "Calculate total cart price using .reduce() (handle empty arrays).",
                "Implement a 'findDuplicate' algorithm using a Set for O(n) performance.",
                "Deep clone a multi-dimensional array without using JSON.parse().",
                "Sort an array of objects by multiple properties (e.g., date then name)."
            ]
        },
        {
            title: "Closures & High-Order Functions",
            questions: [
                "Build a private counter using Closures (increment, decrement, get).",
                "Implement a simple 'Memoize' function to cache expensive calculations.",
                "Write a function that returns another function to greet users in different languages.",
                "Create a 'Debounce' utility to limit how often a function can fire.",
                "Simulate partial application using the .bind() method."
            ]
        },
        {
            title: "Asynchronous JavaScript (Promises/Async)",
            questions: [
                "Fetch data from 'JSONPlaceholder' using both .then() and async/await.",
                "Handle multiple API calls simultaneously using Promise.allSettled().",
                "Implement a custom Promise that resolves after a random delay.",
                "Handle fetch errors gracefully using try/catch blocks and status checks.",
                "Build an auto-retry fetch function that attempts 3 times before failing."
            ]
        },
        {
            title: "Object Prototypes & Classes",
            questions: [
                "Create a 'SmartDevice' class with getters/setters for battery life.",
                "Demonstrate Prototypal Inheritance between 'Animal' and 'Dog'.",
                "Use 'Object.freeze' vs 'Object.seal' and show their differences.",
                "Implement a 'Singleton' pattern for a Configuration object.",
                "Explain the 'this' keyword behavior in Arrow functions vs Regular functions."
            ]
        },
        {
            title: "DOM Manipulation & Events",
            questions: [
                "Build a dynamic 'To-Do' list that adds/removes items via DOM API.",
                "Implement 'Event Delegation' to handle clicks on a dynamically generated list.",
                "Toggle 'Dark Mode' classes on the body using localStorage for persistence.",
                "Detect when a user scrolls to the bottom of a container (Infinite Scroll logic).",
                "Create a live-search filter that updates the DOM as the user types."
            ]
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 8, px: 2 }}>
                <Typography
                    variant={isMobile ? "h4" : "h2"}
                    sx={{
                        fontWeight: 900,
                        color: "#E5C95F",
                        textTransform: "uppercase",
                        letterSpacing: "-1px",
                    }}
                >
                    JS MASTER CHALLENGE
                </Typography>
                <Chip
                    label="PRODEV CONSULT · FINNISH ENGINEERING STANDARDS"
                    sx={{ mt: 2, bgcolor: "rgba(229, 201, 95, 0.1)", color: "#E5C95F", fontWeight: 800, border: "1px solid #E5C95F" }}
                />
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                            <JavascriptIcon sx={{ fontSize: 40, color: "#E5C95F" }} />
                            <Typography variant="h5" fontWeight={800} color="#E5C95F">
                                Technical Evaluation Strategy
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.8, mb: 4, lineHeight: 1.8 }}>
                            Welcome to the final frontier of JavaScript mastery. This assignment contains <strong>30 algorithmic and structural tasks</strong>. Completing these ensures you are prepared for Full-Stack React development and Senior technical interviews.
                        </Typography>

                        <Grid container spacing={2}>
                            {[
                                "Master ES6+ Destructuring & Spread",
                                "Asynchronous Control Flow (Promises)",
                                "Functional Array Prototypes",
                                "DOM Manipulation & Event Loop",
                                "Clean Code & Big O efficiency"
                            ].map((item, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <CheckIcon sx={{ color: "#00ED64", fontSize: 18 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{item}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* ✅ Assignment Modules */}
                {assignmentData.map((topic, index) => (
                    <Grid item xs={12} key={index}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(229, 201, 95, 0.15)", color: "#E5C95F", border: "1px solid #E5C95F" }}>
                                        <TerminalIcon size={22} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: 0.5 }}>
                                        {topic.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1.5}>
                                    <Tooltip title={copiedIndex === index ? "Copied!" : "Copy Tasks"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(topic.questions, index)}
                                            sx={{ color: "white", borderColor: "rgba(255,255,255,0.2)", "&:hover": { borderColor: "#E5C95F" } }}
                                        >
                                            {copiedIndex === index ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<CodeIcon />}
                                            sx={{ bgcolor: "#E5C95F", color: "#100720", fontWeight: 700 }}
                                        >
                                            Dev Environment
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Box sx={{ mt: 2 }}>
                                {topic.questions.map((q, i) => (
                                    <QuestionItem key={i}>
                                        <Typography sx={{ color: "#E5C95F", fontWeight: 900, minWidth: "25px" }}>{i + 1}.</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.85, lineHeight: 1.6 }}>{q}</Typography>
                                    </QuestionItem>
                                ))}
                            </Box>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center" sx={{ mt: 6, mb: 10 }}>
                    <Stack direction={isMobile ? "column" : "row"} spacing={3} justifyContent="center" alignItems="center">
                        <Link to="/javadashboard" style={{ textDecoration: "none" }}>
                            <ActionButton variant="contained">
                                BACK TO DASHBOARD
                            </ActionButton>
                        </Link>
                        {/* Assuming this component exists in your project */}
                        {/* <DownloadPDFButton /> */}
                    </Stack>
                </Grid>
            </Grid>

            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 30, right: 30, bgcolor: "#E5C95F", "&:hover": { bgcolor: "#FFF0A5" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
